/**
 * Card list of saved LLM configurations. Each card shows mode + the
 * key facts (provider/agent, model, auth source) so the user can scan
 * them; buttons open the editor or delete a config.
 */
import type { LlmConfig } from '@/types'
import { PROVIDERS, AGENTS, getProvider, getModel, getAgent } from '@/data/llmCatalog'
import { Pencil, Trash2, Plus, Server, Bot } from 'lucide-react'

interface Props {
  configs: LlmConfig[]
  defaultName: string
  onAdd: () => void
  onEdit: (config: LlmConfig) => void
  onDelete: (name: string) => void
}

function configSummary(c: LlmConfig): string {
  if (c.mode === 'api') {
    const p = getProvider(c.provider)
    const m = getModel(c.provider, c.model)
    return `${p?.label || c.provider} · ${m?.label || c.model || '<no model>'}`
  }
  const a = getAgent(c.agent)
  const authLabel = c.auth === 'subscription' ? 'Subscription account'
    : c.auth === 'configured' ? 'OpenCode providers' : 'API key'
  return `${a?.label || c.agent} · ${authLabel}`
}

function configThinking(c: LlmConfig): string | null {
  if (!c.thinking || c.thinking === 'off') return null
  return `Thinking: ${c.thinking}`
}

export default function LlmConfigList({
  configs,
  defaultName,
  onAdd,
  onEdit,
  onDelete,
}: Props) {
  if (configs.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          border: '1px dashed var(--color-border)',
          borderRadius: 8,
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
        }}
      >
        <p style={{ marginBottom: 12 }}>
          No LLM configurations yet. Add one to start using AI features.
        </p>
        <button className="btn btn-primary btn-sm" onClick={onAdd}>
          <Plus size={14} style={{ marginRight: 4 }} /> Add configuration
        </button>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {configs.map((c) => {
          const isDefault = c.name === defaultName
          const Icon = c.mode === 'api' ? Server : Bot
          return (
            <div
              key={c.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 14px',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                background: 'var(--color-bg-elevated)',
              }}
            >
              <Icon size={18} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</span>
                  {isDefault && (
                    <span className="badge badge-info" style={{ fontSize: 13 }}>
                      Default
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: 13,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'var(--color-bg-primary)',
                      color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    {c.mode}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  {configSummary(c)}
                  {configThinking(c) && (
                    <span style={{ marginLeft: 8 }}>· {configThinking(c)}</span>
                  )}
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => onEdit(c)}
                title="Edit"
                style={{ padding: '4px 8px' }}
              >
                <Pencil size={14} />
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => onDelete(c.name)}
                title="Delete"
                style={{ padding: '4px 8px', color: 'var(--color-danger, #dc2626)' }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          )
        })}
      </div>
      <button
        className="btn btn-secondary btn-sm"
        onClick={onAdd}
        style={{ marginTop: 12 }}
      >
        <Plus size={14} style={{ marginRight: 4 }} /> Add configuration
      </button>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
        Catalog: {PROVIDERS.length} providers · {AGENTS.length} agents
      </div>
    </div>
  )
}
